import { Copy, Printer } from "lucide-react";
import { type FormEvent, useState } from "react";

import { api } from "../../api/client";
import { attentionSelector, designate } from "../../components/attention";
import { Callout } from "../../components/Callout";
import { Dialog, DialogBody } from "../../components/Dialog";
import { Presence } from "../../components/presence";
import { Button, Field } from "../../components/ui";
import { t } from "../../i18n/i18n";
import { FormError, PasswordInput } from "../AuthForm";
import { authDate, authErrorText, authTime } from "../authText";
import { printSlips, type Slip } from "./handover";
import { useReauth } from "./reauth";
import { Waiting } from "../../components/Waiting";

/** Fenêtres communes de l'administration : confirmation du mot de passe (§4.6), code affiché une seule fois avec sa
 *  fiche de remise (§5.3). */

/** « Confirmez votre mot de passe » : ouverte par un refus `REAUTH_REQUIRED`, le geste est rejoué après. */
export function ReauthHost() {
  const pending = useReauth((s) => s.pending);
  return <Presence show={pending !== null}>{pending !== null && <ReauthDialog />}</Presence>;
}

function ReauthDialog() {
  const answer = useReauth((s) => s.answer);
  const [password, setPassword] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!password) return void designate(attentionSelector("admin-reauth"));
    if (busy) return;
    setBusy(true);
    setProblem(null);
    try {
      await api.auth.reauth(password);
      setPassword("");
      answer(true);
    } catch (error) {
      setPassword("");
      setProblem(authErrorText(error, "unlock"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t("Confirmez votre mot de passe")}
      width={380}
      onClose={() => answer(false)}
      footer={
        <>
          <Button variant="secondary" onClick={() => answer(false)}>
            {t("Annuler")}
          </Button>
          <Button type="submit" form="admin-reauth-form" disabled={busy} blocked={!password} onBlocked={() => designate(attentionSelector("admin-reauth"))}>
            {busy ? <Waiting>{t("Vérification…")}</Waiting> : t("Confirmer")}
          </Button>
        </>
      }
    >
      <DialogBody>
        <form id="admin-reauth-form" onSubmit={(e) => void submit(e)} className="space-y-3">
          <p className="text-ui leading-relaxed text-ink-soft">{t("Cette action d'administration demande votre mot de passe.")}</p>
          <Field label={t("Mot de passe")}>
            <PasswordInput value={password} onChange={setPassword} current attention="admin-reauth" autoFocus />
          </Field>
          <FormError message={problem} />
        </form>
      </DialogBody>
    </Dialog>
  );
}

/** Code d'activation ou de réinitialisation affiché une seule fois (§8.3) : copiable, imprimable sur une fiche de
 *  remise, oublié à la fermeture. `reset` : sessions de la personne fermées, mot de passe actuel valable jusqu'à
 *  l'usage du code. */
export function CodeRevealDialog({
  slips,
  kind,
  unit,
  onClose,
}: {
  slips: Slip[];
  kind: "activation" | "reset";
  unit: string | null;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const single = slips.length === 1 ? slips[0]! : null;
  const copy = () => {
    const text = slips.map((slip) => `${slip.login ?? slip.name} ${slip.code}`).join("\n");
    void navigator.clipboard?.writeText(single ? single.code : text).then(
      () => setCopied(true),
      () => setCopied(false),
    );
  };
  const title = single
    ? kind === "reset"
      ? t("Code de réinitialisation de {nom}", { nom: single.name })
      : t("Code d'activation de {nom}", { nom: single.name })
    : t("Codes d'activation de {n} comptes", { n: slips.length });
  return (
    <Dialog
      title={title}
      width={single ? 440 : 600}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={copy} className="mr-auto">
            <Copy size={14} /> {t("Copier")}
          </Button>
          <Button variant="secondary" onClick={() => printSlips(slips, unit)}>
            <Printer size={14} /> {single ? t("Imprimer la fiche de remise") : t("Imprimer les fiches de remise")}
          </Button>
          <Button onClick={onClose}>{t("Fermer")}</Button>
        </>
      }
    >
      <DialogBody>
        {single ? (
          <>
            <p className="text-ui leading-relaxed text-ink-soft">
              {t("Remettez ce code à {nom} en main propre ou de vive voix. Il ne sera plus affiché, ne sert qu'une fois et expire le {date} à {heure}.", {
                nom: single.name,
                date: authDate(single.expiresAt),
                heure: authTime(single.expiresAt),
              })}
            </p>
            <div className="rounded-lg border border-line bg-canvas px-3 py-3 text-center font-mono text-heading tracking-wider text-ink tabular-nums select-all">
              {single.code}
            </div>
            <p className="text-meta text-ink-muted">{t("Identifiant : {identifiant}.", { identifiant: single.login ?? "—" })}</p>
          </>
        ) : (
          <>
            <p className="text-ui leading-relaxed text-ink-soft">
              {t("Remettez à chacun son code en main propre ou de vive voix. Les codes ne seront plus affichés, ne servent qu'une fois et expirent dans 7 jours.")}
            </p>
            <ul>
              {slips.map((slip) => (
                <li key={slip.code} className="grid min-h-8 grid-cols-[1fr_auto] items-center gap-x-3 border-b border-line py-1.5 last:border-b-0">
                  <span className="min-w-0">
                    <span className="block truncate text-ui text-ink">{slip.name}</span>
                    <span className="block text-meta text-ink-muted">{slip.login}</span>
                  </span>
                  <span className="font-mono text-ui tracking-wide text-ink tabular-nums select-all">{slip.code}</span>
                </li>
              ))}
            </ul>
          </>
        )}
        {kind === "reset" && single && (
          <Callout tone="info">
            {t("Les sessions de {nom} ont été fermées. Son mot de passe actuel reste valable jusqu'à l'usage du code. {nom} sera prévenu à sa prochaine connexion que vous avez émis ce code. En cas de compromission, verrouillez d'abord le compte.", {
              nom: single.name,
            })}
          </Callout>
        )}
        <Presence show={copied}>
          {copied && <Callout tone="ok">{t("Copié : effacez-le du presse-papiers une fois remis.")}</Callout>}
        </Presence>
      </DialogBody>
    </Dialog>
  );
}
